import { ErrorBoundary } from 'react-error-boundary';
import { ListView } from '@proposify/list-view';
import { useTranslation } from 'react-i18next';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { trackError } from '@proposify/frontend-observability';
import { useMemo, type PropsWithChildren } from 'react';
import { buildColumns } from './AssignedCustomFields';
import { AutocompleteMonoSelect } from 'components/AutocompleteMonoSelect';
import EmptyState from 'components/emptyState';

export function AssignedCustomFieldsErrorBoundary({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const columns = useMemo(() => buildColumns(t), [t]);

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ resetErrorBoundary }) => (
            <>
              <AutocompleteMonoSelect
                disabled
                options={[]}
                getOptionLabel={() => ''}
                ariaLabel={t('editor.variables.assign_custom_field')}
                placeholder={t('editor.variables.enter_field_name')}
              />
              <ListView
                withHeader
                aria-label={t('editor.variables.assigned_custom_fields')}
                ErrorState={
                  <EmptyState
                    headerText={t('editor.variables.error_loading_custom_fields')}
                    supportingText={t('editor.variables.error_loading_custom_fields_description')}
                    onRetry={resetErrorBoundary}
                  />
                }
                columns={columns}
                rows={[]}
              />
            </>
          )}
          onError={(error, info) => {
            trackError(new Error('AssignedCustomFields rendering failed', { cause: error }), {
              component: 'AssignedCustomFields',
              errorFrom: 'editor-remote',
              feature: 'custom-fields',
              reactErrorInfo: info,
            });
          }}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
